<template>
  <div class="layout-steps page-max-width footer-max-width">
    <QHeader class="bg-white text-primary shadow-soft">
      <QToolbar>
        <QBtn v-if="currentStepProgress > 1" flat round dense icon="icon-f-arrow-left" @click="previousStep()" />
        <QToolbarTitle class="toolbar-subtitled">
          {{ $t(stepTitle) }}
        </QToolbarTitle>
      </QToolbar>
      <ProgressBarSteps v-if="currentStepProgress > 0" :steps="maxStepProgress" :current-step="currentStepProgress" color="accent" />
    </QHeader>

    <QPageContainer>
      <router-view />
    </QPageContainer>
  </div>
</template>

<script setup>
  import { RouterHelpers } from "@core/helpers";
  import { EventManager } from "@core/managers/event.manager";
  import { META_HEADER_TITLE } from "@core/constants";
  import { ETAB_EVENTS, ETAB_ROUTES, META_STEPS_DEFINITIONS, META_BACK_ROUTE, META_PROGRESS } from "@etab/config";
  import { computed, onMounted, ref } from "vue";
  import { useRoute, useRouter } from "vue-router";
  import { useCssTransitionsStore } from "@core/store/modules/cssTransitions";
  import { ACTION_TRANSITIONS_SET } from "@core/store/types";

  const router = useRouter();
  const route = useRoute();
  const cssTransitionsStore = useCssTransitionsStore();

  const backRoute = ref(null);
  const stepsDefinitions = ref(null);
  const maxStepProgress = ref(0);

  // Computed
  const stepTitle = computed(() => {
    // we use store only in the case the page title is not set in routes
    // be sure to reset the page title on beforeDestroy in order to avoid weird data
    return RouterHelpers.getRouteMeta(route, META_HEADER_TITLE);
  });

  const currentStepProgress = computed(() => {
    return route.meta && route.meta[META_PROGRESS];
  });

  const currentStepIndex = computed(() => {
    return stepsDefinitions.value && stepsDefinitions.value.findIndex((step) => step.name === route.name);
  });

  const canNextStep = computed(() => {
    return stepsDefinitions.value && currentStepIndex.value < stepsDefinitions.value.length - 1;
  });

  const canPreviousStep = computed(() => {
    return stepsDefinitions.value && currentStepIndex.value > 0;
  });

  // Methods
  const initStepsLayout = function () {
    stepsDefinitions.value = RouterHelpers.getRouteMeta(route, META_STEPS_DEFINITIONS);
    backRoute.value = RouterHelpers.getRouteMeta(route, META_BACK_ROUTE) || ETAB_ROUTES.creation.root.name;
    maxStepProgress.value = stepsDefinitions.value
      ?.map((step) => step.meta[META_PROGRESS]) // we take the progress meta property
      .filter(Number) // we filter invalid values (it should be a number)
      .reduce((a, b) => Math.max(a, b)); // we take the max of those values
  };

  const nextStep = function ({ step } = {}) {
    // this.transitionsStore[ACTION_TRANSITIONS_SET](TRANSITIONS.SLIDE.RIGHT);
    if (step) {
      router.push(step).catch((suppressedError) => {});
      return;
    }

    if (canNextStep.value) {
      const nextStep = currentStepIndex.value + 1;
      router.push({ name: stepsDefinitions.value[nextStep].name }).catch((suppressedError) => {});
    }
  };

  const previousStep = function () {
    if (canPreviousStep.value) {
      cssTransitionsStore[ACTION_TRANSITIONS_SET]("LEFT");
      router.push({ name: stepsDefinitions.value[currentStepIndex.value - 1].name }).catch((suppressedError) => {});
    } else {
      quitSteps();
    }
  };

  const quitSteps = function () {
    router.push({ name: backRoute }).catch((suppressedError) => {});
  };

  onMounted(() => {
    initStepsLayout();
    EventManager.on(ETAB_EVENTS.STEPS.NEXT_STEP, nextStep);
  });
</script>

<style lang="scss">
  .layout-steps {
    background: $extra-light-grey;
    .q-footer {
      background-color: transparent;
      @media screen and (min-width: $breakpoint-sm-max) {
        margin-bottom: 25px;
        > * {
          padding: 0 25px;
        }
      }
    }

    .step-title,
    .step-title-optional {
      font-family: $common-font-family;
      font-weight: bold;
      letter-spacing: 0.2px;
      text-align: center;
      color: $primary;
    }

    .step-title {
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: 50px;
    }

    .step-title-optional {
      span {
        font-weight: normal;
      }
    }
  }
</style>
